import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AUTH_TOKEN_PROVIDER, AuthTokenProvider } from './auth-token-provider';
import { AuthInterceptorConfig } from './models';
import { AUTH_INTERCEPTOR_CONFIG } from './tokens';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpInterceptor implements HttpInterceptor {
  private readonly _config: AuthInterceptorConfig = inject(
    AUTH_INTERCEPTOR_CONFIG,
  );
  private readonly _tokenProvider: AuthTokenProvider =
    inject(AUTH_TOKEN_PROVIDER);

  private _whitelistedDomains: (string | RegExp)[];

  private _replaceStringsToRegExp(
    arr: Array<string | RegExp>,
  ): Array<string | RegExp> {
    return arr.map((r) => {
      if (typeof r === 'string' && r.startsWith('^')) {
        return new RegExp(r);
      }
      return r;
    });
  }

  private _getWhitelistedDomains() {
    if (!this._whitelistedDomains) {
      this._whitelistedDomains = this._config.whitelistedDomains
        ? this._replaceStringsToRegExp(this._config.whitelistedDomains)
        : [];
    }
    return this._whitelistedDomains;
  }

  private _isWhitelistedDomain(request: HttpRequest<any>): boolean {
    try {
      const requestUrl: URL = new URL(request.url);
      const host = requestUrl.host.toLowerCase();
      return this._getWhitelistedDomains().some((domain) =>
        typeof domain === 'string'
          ? domain === host
          : domain instanceof RegExp
            ? domain.test(host)
            : false,
      );
    } catch {
      // local / relative URL
      return false;
    }
  }

  private _isTokenEndpointRequest(request: HttpRequest<any>): boolean {
    const tokenEndpoint = this._tokenProvider.getTokenEndpoint();
    return tokenEndpoint
      ? request.url.toLowerCase().startsWith(tokenEndpoint.toLowerCase())
      : false;
  }

  private _handleInterception(
    token: string,
    request: HttpRequest<any>,
    next: HttpHandler,
  ) {
    let tokenIsExpired = false;

    if (!token && this._config.throwNoTokenError) {
      throw new Error('Could not get token from token provider.');
    }

    if (this._config.skipWhenExpired) {
      tokenIsExpired = token ? !this._tokenProvider.isAccessTokenValid() : true;
    }

    if (token && (!tokenIsExpired || !this._config.skipWhenExpired)) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return next.handle(request);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isWhitelisted = this._isWhitelistedDomain(request);
    const isTokenEndpoint = this._isTokenEndpointRequest(request);
    if (!isWhitelisted || isTokenEndpoint) {
      return next.handle(request);
    }

    const token = this._tokenProvider.getAccessToken();

    if (token instanceof Promise) {
      return from(token).pipe(
        mergeMap((asyncToken: string) =>
          this._handleInterception(asyncToken, request, next),
        ),
      );
    } else {
      return this._handleInterception(token, request, next);
    }
  }
}
